import type { ComponentType } from 'react';
import type { PostMeta, PostEntry } from './types';

/**
 * Vite glob import — picks up every .mdx file in src/blog/posts/.
 * Each module exports a default React component and a named `meta` export.
 *
 * The `eager: false` (default) keeps each post as a separate lazy chunk
 * so only the post the reader navigates to is downloaded.
 */
const modules = import.meta.glob<{
  default: ComponentType;
  meta: PostMeta;
  readingTime?: number;
}>('./posts/*.mdx');

/**
 * Eager raw import of each post's MDX source.
 * Used to provide full post content to the AI explainer as context.
 * The raw strings are small (~few KB each) and are included in the bundle.
 */
const rawModules = import.meta.glob<string>('./posts/*.mdx', {
  query: '?raw',
  import: 'default',
  eager: true,
});

/**
 * Derive a slug from the filename.
 * Files are expected to be named `YYYY-MM-DD-slug.mdx` or `slug.mdx`.
 * The slug is whatever comes after the optional date prefix.
 *
 * e.g. "./posts/2026-02-25-intro.mdx" → "intro"
 *      "./posts/parametric-boxes.mdx" → "parametric-boxes"
 */
function slugFromPath(path: string): string {
  const filename = path.replace('./posts/', '').replace('.mdx', '');
  // Strip optional YYYY-MM-DD- date prefix
  return filename.replace(/^\d{4}-\d{2}-\d{2}-/, '');
}

/**
 * Returns all post entries with their metadata, sorted newest-first.
 * Metadata is loaded eagerly (it's just a small plain object per post).
 */
export async function getAllPosts(): Promise<PostEntry[]> {
  const entries = await Promise.all(
    Object.entries(modules).map(async ([path, load]) => {
      const mod = await load();
      return {
        meta: {
          ...mod.meta,
          slug: slugFromPath(path),
          readingTime: mod.readingTime,
        },
        rawContent: rawModules[path] ?? '',
        load,
      } satisfies PostEntry;
    }),
  );

  return entries
    .filter((e) => e.meta.published === true)
    .sort(
      (a, b) =>
        new Date(b.meta.date).getTime() - new Date(a.meta.date).getTime(),
    );
}

/**
 * Returns the previous and next PostMeta for a given slug.
 * Posts are ordered newest-first, so "next" is the newer neighbour and
 * "prev" is the older one — matching typical blog navigation conventions.
 */
export async function getAdjacentPosts(
  slug: string,
): Promise<{ prev: PostMeta | null; next: PostMeta | null }> {
  const all = await getAllPosts(); // already sorted newest-first
  const idx = all.findIndex((e) => e.meta.slug === slug);
  if (idx === -1) return { prev: null, next: null };
  return {
    next: idx > 0 ? all[idx - 1].meta : null, // newer
    prev: idx < all.length - 1 ? all[idx + 1].meta : null, // older
  };
}

/**
 * Returns the PostEntry for a given slug, or null if not found.
 */
export async function getPost(slug: string): Promise<PostEntry | null> {
  const entry = Object.entries(modules).find(
    ([path]) => slugFromPath(path) === slug,
  );
  if (!entry) return null;

  const [path, load] = entry;
  const mod = await load();
  return {
    meta: {
      ...mod.meta,
      slug: slugFromPath(path),
      readingTime: mod.readingTime,
    },
    rawContent: rawModules[path] ?? '',
    load,
  };
}
